import type { TOC } from '@ember/component/template-only';
import { Text } from 'tio-ui/components/typography';
import { eq } from 'tio-ui/utilities';
import type { ComponentLike } from '@glint/template';

/**
 * Color theme options for the summary stat card value
 */
export type SummaryStatColor =
  | 'default'
  | 'violet'
  | 'green'
  | 'amber'
  | 'teal'
  | 'red'
  | 'blue'
  | 'emerald'
  | 'indigo';

/**
 * Trend direction for the stat
 */
export type TrendDirection = 'up' | 'down';

/**
 * SummaryStatCard - A compact stat card for dashboard summary grids
 *
 * Displays a simple label and value, designed for summary stat grids
 * like those found in admin dashboards.
 *
 * @example Basic usage
 * ```gts
 * <SummaryStatCard @label="Direct Reports" @value="8" />
 * ```
 *
 * @example With icon
 * ```gts
 * <SummaryStatCard @label="In Pipeline" @value="124" @icon={{PhUsers}} @iconColor="violet" />
 * ```
 *
 * @example With trend
 * ```gts
 * <SummaryStatCard @label="YTD" @value="18" @trend="up" @trendText="+12% YoY" />
 * ```
 *
 * @example With secondary text
 * ```gts
 * <SummaryStatCard @label="Ready Now" @value="18" @secondaryText="14% of pipeline" />
 * ```
 */
interface SummaryStatCardSignature {
  Args: {
    /** Label text displayed above the value */
    label: string;
    /** Main value to display */
    value: string | number;
    /** Optional suffix (e.g., "%" or "mo") */
    suffix?: string;
    /** Color theme for the value */
    color?: SummaryStatColor;
    /** Optional icon component */
    icon?: ComponentLike<{ Args: { size: number }; Element: SVGElement }>;
    /** Icon color theme */
    iconColor?: SummaryStatColor;
    /** Trend direction (shows trend icon) */
    trend?: TrendDirection;
    /** Trend text (e.g., "+12% YoY") */
    trendText?: string;
    /** Secondary text below value */
    secondaryText?: string;
  };
  Element: HTMLDivElement;
}

const valueColorClasses: Record<SummaryStatColor, string> = {
  default: 'text-gray-900 dark:text-white',
  violet: 'text-violet-600 dark:text-violet-400',
  green: 'text-green-600 dark:text-green-400',
  amber: 'text-amber-600 dark:text-amber-400',
  teal: 'text-teal-600 dark:text-teal-400',
  red: 'text-red-600 dark:text-red-400',
  blue: 'text-blue-600 dark:text-blue-400',
  emerald: 'text-emerald-600 dark:text-emerald-400',
  indigo: 'text-indigo-600 dark:text-indigo-400',
};

const iconColorClasses: Record<SummaryStatColor, string> = {
  default: 'text-gray-600',
  violet: 'text-violet-600',
  green: 'text-green-600',
  amber: 'text-amber-600',
  teal: 'text-teal-600',
  red: 'text-red-600',
  blue: 'text-blue-600',
  emerald: 'text-emerald-600',
  indigo: 'text-indigo-600',
};

const getValueColorClass = (color: SummaryStatColor = 'default') => valueColorClasses[color];
const getIconColorClass = (color: SummaryStatColor = 'default') => iconColorClasses[color];

const SummaryStatCard: TOC<SummaryStatCardSignature> = <template>
  <div
    class="bg-white dark:bg-gray-800 rounded-lg p-4 shadow-sm border border-gray-200 dark:border-gray-700"
    ...attributes
  >
    {{#if @icon}}
      <div class="flex items-center gap-2 mb-2">
        <@icon @size={{18}} class="{{getIconColorClass @iconColor}}" />
        <Text @intent="muted" @size="sm">{{@label}}</Text>
      </div>
    {{else}}
      <Text class="text-gray-500 dark:text-gray-400 text-sm mb-1">{{@label}}</Text>
    {{/if}}
    <div class="text-2xl font-bold {{getValueColorClass @color}}">
      {{@value}}{{if @suffix @suffix}}
    </div>
    {{#if @trend}}
      <div class="flex items-center gap-1 mt-1">
        {{#if (eq @trend "up")}}
          <svg class="w-3.5 h-3.5 text-green-600" fill="currentColor" viewBox="0 0 20 20"><path
              fill-rule="evenodd"
              d="M5.293 7.707a1 1 0 010-1.414l4-4a1 1 0 011.414 0l4 4a1 1 0 01-1.414 1.414L11 5.414V17a1 1 0 11-2 0V5.414L6.707 7.707a1 1 0 01-1.414 0z"
              clip-rule="evenodd"
            /></svg>
        {{else if (eq @trend "down")}}
          <svg class="w-3.5 h-3.5 text-red-600" fill="currentColor" viewBox="0 0 20 20"><path
              fill-rule="evenodd"
              d="M14.707 12.293a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 111.414-1.414L9 14.586V3a1 1 0 012 0v11.586l2.293-2.293a1 1 0 011.414 0z"
              clip-rule="evenodd"
            /></svg>
        {{/if}}
        <Text
          @size="sm"
          class="{{if (eq @trend 'up') 'text-green-600 dark:text-green-400'}}{{if
              (eq @trend 'down')
              'text-red-600 dark:text-red-400'
            }}"
        >{{@trendText}}</Text>
      </div>
    {{else if @secondaryText}}
      <Text @size="sm" class="text-gray-500 dark:text-gray-400">{{@secondaryText}}</Text>
    {{/if}}
  </div>
</template>;

export default SummaryStatCard;
